// Defines the 6 permission sections used in the Employee Management wizard.
// Each `sub.role` is the actual ROLE_* string consumed by the backend / RBAC layer.
// `parentRole` (when present) is auto-added when ANY sub of that section is enabled,
// preserving the parent/child invariants the rest of the codebase relies on.

export const SECTION_KEYS = {
  SECURITY: 'security',
  DASHBOARD: 'dashboard',
  OPERATION_MONITOR: 'operationMonitor',
  READING: 'reading',
  ACCOUNTING: 'accounting',
  SETTINGS: 'settings',
};

export const PERMISSION_SECTIONS = [
  {
    key: SECTION_KEYS.SECURITY,
    label: 'Security',
    number: 1,
    subs: [
      { key: 'securityDashboard', label: 'Security Dashboard', role: 'ROLE_SECURITY' },
      { key: 'walletPass', label: 'Customer Wallet Pass', role: 'ROLE_CUSTOMER_WALLET_PASS' },
    ],
  },
  {
    key: SECTION_KEYS.DASHBOARD,
    label: 'Dashboard',
    number: 2,
    subs: [
      { key: 'customerAccess', label: 'Customer Access', role: 'ROLE_CUSTOMER' },
      { key: 'transaction', label: 'Transaction', role: 'ROLE_TRANSACTION' },
      { key: 'ticketOut', label: 'Ticket Out (NFC in Pro Elite plan)', role: 'ROLE_TICKET_OUT', extraRoles: ['ROLE_TICKET_NFC'] },
      { key: 'bonus', label: 'Bonus', role: 'ROLE_BONUS' },
      { key: 'raffle', label: 'Raffle', role: 'ROLE_RAFFLE' },
      { key: 'note', label: 'Note', role: 'ROLE_NOTE' },
      { key: 'checkInOut', label: 'Check In / Out', role: 'ROLE_CHECK_IN_OUT' },
    ],
  },
  {
    key: SECTION_KEYS.OPERATION_MONITOR,
    label: 'Operations Monitor',
    number: 3,
    subs: [
      { key: 'pointWatching', label: 'Point Watching', role: 'ROLE_POINT_WATCHING' },
      { key: 'employeeSession', label: 'Employee Session', role: 'ROLE_EMPLOYEE_SESSION' },
    ],
  },
  {
    key: SECTION_KEYS.READING,
    label: 'Reading',
    number: 4,
    parentRole: 'ROLE_READING',
    subs: [
      { key: 'machinePicture', label: 'Machine Reading Pictures', role: 'ROLE_MACHINE_PICTURE' },
      { key: 'addReading', label: 'Start New Reading Session', role: 'ROLE_ADD_MACHINE_READING' },
      { key: 'masterReading', label: 'Reading Sessions Record', role: 'ROLE_MASTER_READING' },
    ],
  },
  {
    key: SECTION_KEYS.ACCOUNTING,
    label: 'Accounting',
    number: 5,
    parentRole: 'ROLE_ACCOUNTING',
    subs: [
      { key: 'weeklyReport', label: 'Weekly Report', role: 'ROLE_REPORT' },
      { key: 'atmTransaction', label: 'ATM Transaction', role: 'ROLE_ATM_TRANSACTION' },
      { key: 'openAllWeekly', label: 'Open All Weekly Report', role: 'ROLE_OPEN_ALL_WEEKLY_REPORT' },
      { key: 'customerLoan', label: 'Customer Loan Report', role: 'ROLE_CUSTOMER_LOAN_REPORT' },
      { key: 'employeeShort', label: 'Employee Short Report', role: 'ROLE_EMPLOYEE_SHORT_REPORT' },
      { key: 'zelle', label: 'Zelle Report', role: 'ROLE_ZELLE_REPORT' },
    ],
  },
  {
    key: SECTION_KEYS.SETTINGS,
    label: 'Settings',
    number: 6,
    parentRole: 'ROLE_SETTINGS',
    subs: [
      { key: 'rules', label: 'Rules', role: 'ROLE_RULES' },
      // "Setup Machines" is presented as a single toggle in the wizard
      // but grants three backend roles together: ROLE_MACHINE (machines
      // page), ROLE_GAME (games page), and ROLE_ASSIGN_MACHINE (the
      // game-to-machine assignment page). The `extraRoles` field is a
      // convention understood by getAuthorities / buildStateFromAuthorities.
      {
        key: 'setupMachines',
        label: 'Setup Machines',
        role: 'ROLE_MACHINE',
        extraRoles: ['ROLE_GAME', 'ROLE_ASSIGN_MACHINE'],
      },
      { key: 'manageBonus', label: 'Manage Bonus', role: 'ROLE_MANAGE_BONUS' },
      { key: 'manageUsers', label: 'Manage Employees', role: 'ROLE_MANAGE_USERS' },
      { key: 'maintenancePermit', label: 'Maintenance Permit', role: 'ROLE_MAINTENANCE_PERMIT' },
    ],
  },
];

export const SECTION_BY_KEY = PERMISSION_SECTIONS.reduce((acc, s) => {
  acc[s.key] = s;
  return acc;
}, {});

// Lookup: ROLE_* string -> { sectionKey, subKey }
export const ROLE_TO_SUB = (() => {
  const map = {};
  PERMISSION_SECTIONS.forEach((section) => {
    section.subs.forEach((sub) => {
      map[sub.role] = { sectionKey: section.key, subKey: sub.key };
    });
  });
  return map;
})();
